/**
 * Главный компонент системы миграций
 */

import { EventEmitter } from 'events';
import { MigrationManager } from './MigrationManager';
import { MigrationRunner, MigrationRunnerConfig, MigrationExecutionInfo } from './MigrationRunner';
import { MigrationScheduler, MigrationSchedulerConfig, MigrationTask } from './MigrationScheduler';
import { MigrationLogger, MigrationLoggerConfig } from './MigrationLogger';
import { MemoryMigrationStorage } from './MemoryMigrationStorage';
import { VersionUtils } from './VersionUtils';
import { 
  IMigrationStorage, 
  IVersionUtils, 
  IMigration
} from '../types/migration-interfaces';

/**
 * Конфигурация системы миграций
 */
export interface MigrationSystemConfig {
  // Конфигурация выполнения миграций
  runner?: MigrationRunnerConfig;
  
  // Конфигурация планировщика
  scheduler?: MigrationSchedulerConfig;
  
  // Конфигурация логирования
  logger?: MigrationLoggerConfig;
  
  // Кастомное хранилище
  storage?: IMigrationStorage;
  
  // Кастомные утилиты версий
  versionUtils?: IVersionUtils;
  
  // Автоматический старт при инициализации
  autoStart?: boolean;
  
  // Интеграция с существующими системами
  integration?: {
    // Хук для инициализации
    onInit?: () => Promise<void>;
    
    // Хук для завершения работы
    onShutdown?: () => Promise<void>;
    
    // Хук для обработки ошибок
    onError?: (error: Error) => Promise<void>;
  };
}

/**
 * Статус системы миграций
 */
export interface MigrationSystemStatus {
  // Статус компонентов
  manager: {
    initialized: boolean;
    migrationsCount: number;
    currentVersion: string;
  };
  
  runner: {
    isExecuting: boolean;
    currentExecution?: MigrationExecutionInfo;
  };
  
  scheduler: {
    isRunning: boolean;
    tasksCount: number;
    stats: {
      totalTasks: number;
      pendingTasks: number;
      runningTasks: number;
      completedTasks: number;
      failedTasks: number;
    };
  };
  
  logger: {
    totalEntries: number;
    stats: {
      totalEntries: number;
      byLevel: Record<string, number>;
      bySource: Record<string, number>;
    };
  };
}

/**
 * Главная система миграций
 */
export class MigrationSystem extends EventEmitter {
  private config: MigrationSystemConfig;
  private manager: MigrationManager;
  private runner: MigrationRunner;
  private scheduler: MigrationScheduler;
  private logger: MigrationLogger;
  private storage: IMigrationStorage;
  private versionUtils: IVersionUtils;
  private isInitialized: boolean = false;
  private isShuttingDown: boolean = false;

  constructor(config: MigrationSystemConfig = {}) {
    super();
    this.config = {
      autoStart: true,
      ...config
    };

    // Инициализируем компоненты
    this.storage = config.storage || new MemoryMigrationStorage();
    this.versionUtils = config.versionUtils || new VersionUtils();
    this.logger = new MigrationLogger(config.logger);
    this.manager = new MigrationManager(this.storage, this.versionUtils);
    this.runner = new MigrationRunner(this.manager, config.runner);
    this.scheduler = new MigrationScheduler(this.manager, config.scheduler);

    this.setupEventHandlers();
  }

  /**
   * Настройка обработчиков событий
   */
  private setupEventHandlers(): void {
    // События менеджера
    this.manager.on('migration:start', async ({ migration, context }) => {
      await this.logger.logMigrationStart(migration);
      this.emit('migration:start', { migration, context });
    });

    this.manager.on('migration:complete', async ({ migration, result }) => {
      await this.logger.logMigrationSuccess(migration, result);
      this.emit('migration:complete', { migration, result });
    });

    this.manager.on('migration:error', async ({ migration, error }) => {
      await this.logger.logMigrationError(migration, error);
      this.emit('migration:error', { migration, error });
      
      // Вызываем хук обработки ошибок
      if (this.config.integration?.onError) {
        try {
          await this.config.integration.onError(error);
                 } catch (hookError) {
           await this.logger.error('system', `Integration error hook failed: ${hookError instanceof Error ? hookError.message : String(hookError)}`);
        }
      }
    });

    // События выполнения
    this.runner.on('execution:start', async (info) => {
      await this.logger.logExecutionInfo(info);
      this.emit('execution:start', info);
    });

    this.runner.on('execution:complete', async (info) => {
      await this.logger.logExecutionInfo(info);
      this.emit('execution:complete', info);
    });

    this.runner.on('execution:cancelled', async () => {
      await this.logger.warn('runner', 'Migration execution was cancelled');
      this.emit('execution:cancelled');
    });

    // События планировщика
    this.scheduler.on('task:start', async (task) => {
      await this.logger.logTaskStart(task);
      this.emit('task:start', task);
    });

    this.scheduler.on('task:complete', async (task) => {
      await this.logger.logTaskComplete(task);
      this.emit('task:complete', task);
    });

    this.scheduler.on('task:error', async ({ task, error }) => {
      await this.logger.error('scheduler', `Task ${task.id} failed: ${error.message}`);
      this.emit('task:error', { task, error });
    });

    this.scheduler.on('scheduler:started', async () => {
      await this.logger.info('scheduler', 'Migration scheduler started');
      this.emit('scheduler:started');
    });

    this.scheduler.on('scheduler:stopped', async () => {
      await this.logger.info('scheduler', 'Migration scheduler stopped');
      this.emit('scheduler:stopped');
    });

    // События логгера
    this.logger.on('log', (entry) => {
      this.emit('log', entry);
    });

    // Обработка ошибок процесса
    process.on('SIGINT', () => this.shutdown());
    process.on('SIGTERM', () => this.shutdown());
    process.on('uncaughtException', async (error) => {
      await this.logger.error('system', `Uncaught exception: ${error.message}`, { stack: error.stack });
      await this.shutdown();
    });
  }

  /**
   * Инициализация системы
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      await this.logger.info('system', 'Initializing migration system');

      // Вызываем хук инициализации
      if (this.config.integration?.onInit) {
        await this.config.integration.onInit();
      }

      // Проверяем целостность системы
      const isValid = await this.manager.validateIntegrity();
      if (!isValid) {
        await this.logger.warn('system', 'Migration integrity check failed');
      }

      // Запускаем планировщик если настроено
      if (this.config.autoStart) {
        await this.scheduler.start();
      }

      this.isInitialized = true;
      await this.logger.info('system', 'Migration system initialized successfully');
      this.emit('system:initialized');

         } catch (error) {
       const errorMessage = error instanceof Error ? error.message : String(error);
       await this.logger.error('system', `Failed to initialize migration system: ${errorMessage}`);
       this.emit('system:error', error);
       throw error;
     }
  }

  /**
   * Завершение работы системы
   */
  async shutdown(): Promise<void> {
    if (this.isShuttingDown) {
      return;
    }

    this.isShuttingDown = true;

    try {
      await this.logger.info('system', 'Shutting down migration system');

      // Останавливаем планировщик
      if (this.scheduler.isRunning()) {
        await this.scheduler.stop();
      }

      // Отменяем выполнение миграций
      if (this.runner.isExecuting()) {
        await this.runner.cancel();
      }

      // Вызываем хук завершения
      if (this.config.integration?.onShutdown) {
        await this.config.integration.onShutdown();
      }

      // Закрываем логгер
      await this.logger.close();

      this.isInitialized = false;
      await this.logger.info('system', 'Migration system shutdown completed');
      this.emit('system:shutdown');

         } catch (error) {
       const errorMessage = error instanceof Error ? error.message : String(error);
       await this.logger.error('system', `Error during shutdown: ${errorMessage}`);
       this.emit('system:error', error);
     }
  }

  /**
   * Регистрация миграции
   */
  registerMigration(migration: IMigration): void {
    this.manager.registerMigration(migration);
  }

  /**
   * Регистрация множества миграций
   */
  registerMigrations(migrations: IMigration[]): void {
    for (const migration of migrations) {
      this.manager.registerMigration(migration);
    }
  }

  /**
   * Ручной запуск миграций
   */
  async runMigrations(): Promise<MigrationExecutionInfo> {
    return this.runner.runPendingMigrations();
  }

  /**
   * Откат миграций
   */
  async rollback(targetVersion?: string): Promise<MigrationExecutionInfo> {
    return this.runner.rollback({
      strategy: 'immediate',
      targetVersion
    });
  }

  /**
   * Планирование задачи миграции
   */
  scheduleTask(): string {
    return this.scheduler.scheduleTask('manual');
  }

  /**
   * Получение статуса системы
   */
  async getStatus(): Promise<MigrationSystemStatus> {
    const managerStatus = await this.manager.checkStatus();
    const runnerExecution = this.runner.getCurrentExecution();
    const schedulerStats = this.scheduler.getStats();
    const loggerStats = this.logger.getStats();

    return {
      manager: {
        initialized: this.isInitialized,
        migrationsCount: this.manager.getMigrations().length,
        currentVersion: managerStatus.currentVersion
      },
      runner: {
        isExecuting: this.runner.isExecuting(),
        currentExecution: runnerExecution
      },
      scheduler: {
        isRunning: this.scheduler.isRunning(),
        tasksCount: this.scheduler.getTasks().length,
        stats: schedulerStats
      },
      logger: {
        totalEntries: loggerStats.totalEntries,
        stats: loggerStats
      }
    };
  }

  /**
   * Получение истории миграций
   */
  async getHistory() {
    return this.manager.getHistory();
  }

  /**
   * Получение логов
   */
  getLogs(filter?: Parameters<typeof this.logger.getEntries>[0]) {
    return this.logger.getEntries(filter);
  }

  /**
   * Получение задач планировщика
   */
  getTasks(): MigrationTask[] {
    return this.scheduler.getTasks();
  }

  /**
   * Отмена задачи
   */
  async cancelTask(taskId: string): Promise<boolean> {
    return this.scheduler.cancelTask(taskId);
  }

  /**
   * Очистка старых задач
   */
  cleanupTasks(olderThan: Date): number {
    return this.scheduler.cleanupTasks(olderThan);
  }

  /**
   * Очистка логов
   */
  clearLogs(): void {
    this.logger.clear();
  }

  /**
   * Сброс системы (для тестирования)
   */
  async reset(): Promise<void> {
    await this.manager.reset();
    this.logger.clear();
    
    // Очищаем задачи
    const tasks = this.scheduler.getTasks();
    for (const task of tasks) {
      if (task.status === 'pending' || task.status === 'running') {
        await this.scheduler.cancelTask(task.id);
      }
    }
  }

  /**
   * Проверка инициализации
   */
  isReady(): boolean {
    return this.isInitialized && !this.isShuttingDown;
  }

  /**
   * Получение компонентов (для расширенного использования)
   */
  getComponents() {
    return {
      manager: this.manager,
      runner: this.runner,
      scheduler: this.scheduler,
      logger: this.logger,
      storage: this.storage,
      versionUtils: this.versionUtils
    };
  }
} 